"use client";

import Link from "next/link";
import { Fragment, type ReactNode, useMemo } from "react";

import { Breadcrumb } from "../breadcrumb";
import { Separator } from "../separator";

export interface LayoutBreadcrumbProps {
  breadcrumbs: { label: ReactNode; url?: string }[];
  appName?: string;
  appUrl?: string;
}

const TRUNCATE_BREADCRUMB_ITEM: { label: string; url: string } = {
  label: "...",
  url: "",
};

// The truncation placeholder and the first item keep their natural width; the last one shrinks first, the others cap.
const itemClassName = ({
  isFirst,
  isLast,
  isPlaceholder,
}: {
  isFirst: boolean;
  isLast: boolean;
  isPlaceholder: boolean;
}) => {
  if (isPlaceholder) {
    return undefined;
  }
  if (isLast) {
    return "min-w-0 shrink truncate";
  }
  return isFirst ? undefined : "max-w-[30%] truncate";
};

const LayoutBreadcrumb = ({ breadcrumbs, appName, appUrl }: LayoutBreadcrumbProps) => {
  const items = useMemo(() => {
    return breadcrumbs.length >= 4
      ? ([breadcrumbs[0], TRUNCATE_BREADCRUMB_ITEM, ...breadcrumbs.slice(-2)] as { label: string; url: string }[])
      : breadcrumbs;
  }, [breadcrumbs]);

  return (
    <div className="flex min-w-0 items-center">
      {appName && (
        <div className="flex shrink-0 gap-4">
          {appUrl ? (
            <Link href={appUrl}>
              <div className="cursor-pointer text-sm font-semibold text-neutral-10">{appName}</div>
            </Link>
          ) : (
            <div className="text-sm font-semibold text-neutral-10">{appName}</div>
          )}
          {breadcrumbs.length > 0 && <Separator orientation="vertical" className="h-4" />}
        </div>
      )}

      {!!breadcrumbs?.length && (
        <Breadcrumb.Root className="min-w-0 border-none">
          <Breadcrumb.List>
            {items.map((item, index) => {
              const isFirst = index === 0;
              const isLast = index === items.length - 1;
              const isTruncatePlaceholder = item === TRUNCATE_BREADCRUMB_ITEM;
              const flexClassName = itemClassName({ isFirst, isLast, isPlaceholder: isTruncatePlaceholder });

              return isLast ? (
                <Fragment key={`${index}-${item.url ?? ""}`}>
                  {index !== 0 && <Breadcrumb.Separator />}

                  <Breadcrumb.Current className={flexClassName}>
                    {item.url ? <Link href={item.url}>{item.label}</Link> : item.label}
                  </Breadcrumb.Current>
                </Fragment>
              ) : (
                <Fragment key={`${index}-${item.url ?? ""}`}>
                  {index !== 0 && <Breadcrumb.Separator />}

                  <Breadcrumb.Item className={flexClassName}>
                    {item.url ? <Link href={item.url}>{item.label}</Link> : item.label}
                  </Breadcrumb.Item>
                </Fragment>
              );
            })}
          </Breadcrumb.List>
        </Breadcrumb.Root>
      )}
    </div>
  );
};

export default LayoutBreadcrumb;
